import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import { DepartmentForm, DepartmentFormValues } from 'forms/DepartmentForm'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useDeleteCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useSelection } from 'hooks/useSelection'
import { pick } from 'lodash'
import { Department, DepartmentInsertInput, UserRole } from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import {
  useCreateDepartmentMutation,
  useDeleteDepartmentMutation,
  useReadDepartmentsQuery,
  useUpdateDepartmentMutation,
} from './AdminDepartmentView.generated'
import { DepartmentList } from './DepartmentList'

const AdminDepartmentListView: React.FC = () => {
  const { t } = useTranslation()

  const [createDepartment] = useCreateDepartmentMutation()
  const [updateDepartment] = useUpdateDepartmentMutation()
  const [deleteDepartments] = useDeleteDepartmentMutation()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()
  const [dialogState, showDialog, hideDialog] = useDialogState()

  const { loading, data, error, refetch } = useReadDepartmentsQuery()

  const [panelState, showPanel, hidePanel] = useFormikPanelState()

  const items =
    data && data.departments ? data.departments.filter((x) => !x._deleted) : []

  const [selection, selectedItems] = useSelection<Department>({
    getKey: (item) => item._id!,
  })

  useCreateCommand('department', {
    priority: 10,
    onClick: () => {
      setFormType(FormType.insert)
      showPanel()
    },
  })

  useEditCommand(
    { name: 'department', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], ['id', '__typename']) as Department
        )
      },
    },
    [selectedItems, setFormType]
  )

  useDeleteCommand(
    { name: 'department', count: selectedItems.length },
    {
      priority: 30,
      onClick: showDialog,
    },
    [selectedItems]
  )

  const handleSubmit = async (
    values: DepartmentFormValues,
    formikActions: FormikActions<DepartmentFormValues>
  ) => {
    formikActions.setSubmitting(true)
    if (formType === FormType.insert) {
      await createDepartment({
        variables: { input: values as DepartmentInsertInput },
      })
    } else if (formType === FormType.update) {
      await updateDepartment({
        variables: {
          id: selectedItemId,
          update: pick(values, ['department', 'headOfDepartment']),
        },
      })
    } else {
      throw new Error('ERROR')
    }

    formikActions.setSubmitting(false)
    refetch()
    hidePanel()
  }

  const handleConfirmDelete = React.useCallback(async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((ft) => ft._id)
      await deleteDepartments({
        variables: { ids },
      })
    }
    hideDialog()
    refetch()
  }, [selectedItems, deleteDepartments, refetch, hideDialog])

  return (
    <FlexSpinner loading={loading} error={error}>
      <DepartmentList items={items} selection={selection} />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('field.Department.edit.label')
            : t('field.Department.create.label')
        }
      >
        <DepartmentForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminDepartmentView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.ResourceManager, UserRole.SystemAdministrator]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminDepartmentListView />
    </StickyPane>
  </ViewAuthorizer>
)
